/**
 * 일정 캘린더 서비스 — REST 클라이언트 + 범위 캐시(모듈 스토어) + 화면 요청 스토어.
 *
 * 서버 계약: server/routes/calendar.js. 반복 회차는 서버가 펼쳐 준다(여기서 전개하지 않는다).
 *
 * 캐시
 *  - 키는 'from|to'(KST 날짜 키). 홈 미니 캘린더·월 보기·목록 보기가 모두 **월 격자 42일** 범위를 쓰므로
 *    같은 달이면 같은 키다 — 홈 카드가 두 벌(데스크톱 목록 패널·모바일)로 마운트돼도 요청은 1건이다.
 *  - 'calendar:changed'(서버 권위, 내용 없음)·재연결·내 쓰기 성공이 캐시를 낡음으로 만들고, **구독 중인 범위만**
 *    다시 받는다(디바운스 300ms). 회사 일정 변경은 계열사 전원에게 동시에 가므로 0~2초 무작위 지연을 더한다.
 *  - 창을 10분 넘게 숨겼다 다시 보이면 다시 받는다 — 인트라넷 부서별일정은 실시간 신호가 없다.
 *  - 로그아웃 시 clearCalendarCache()로 계정 격리(App.handleLogout).
 *
 * 화면 요청 스토어
 *  - 편집기·상세·삭제·공유 창은 App 루트의 CalendarModalsHost 한 곳에서만 렌더한다(모바일 홈은 PullToRefresh
 *    transform 안이라 그 안에서 연 fixed 모달은 탭바 밑에 깔린다). 어디서든 requestOpen*()으로 연다 —
 *    콜백 prop을 MessageItem까지 내려 memo 계약 표면을 넓히지 않기 위해서다.
 */
import { useEffect, useRef, useState } from 'react';
import { apiFetch } from './apiClient';
import {
  ActivityRef, CalendarEventDetail, CalendarRangeResult, CalendarRepeatFreq, CalendarResponse, CalendarScope,
} from '../types';

// ─── REST ───────────────────────────────────────────────────────────────

export interface CalendarEventBody {
  scope?: CalendarScope;
  companyAffiliateId?: string | null;
  title: string;
  location?: string;
  description?: string;
  allDay: boolean;
  startDate?: string;
  endDate?: string;
  start?: string;
  end?: string;
  repeat?: { freq: CalendarRepeatFreq; until?: string | null } | null;
  remindOffset?: number | null;
  attendees?: string[];
  baseUpdatedAt?: string;
  shareChannelId?: number;
}

export interface ShareResult { channelId: number; ok: boolean; error?: string; messageId?: string }

export const calendarApi = {
  getRange(from: string, to: string): Promise<CalendarRangeResult> {
    return apiFetch<CalendarRangeResult>(`/calendar/events?from=${from}&to=${to}`);
  },
  async getEvent(id: string, viaMessageId?: string | number | null): Promise<CalendarEventDetail> {
    const q = viaMessageId ? `?via=${encodeURIComponent(String(viaMessageId))}` : '';
    const res = await apiFetch<{ event: CalendarEventDetail }>(`/calendar/events/${encodeURIComponent(id)}${q}`);
    return res.event;
  },
  async create(body: CalendarEventBody): Promise<{ event: CalendarEventDetail; share: ShareResult | null }> {
    const res = await apiFetch<{ event: CalendarEventDetail; share: ShareResult | null }>('/calendar/events', {
      method: 'POST', body: JSON.stringify(body),
    });
    invalidateCalendar();
    return res;
  },
  async update(id: string, body: CalendarEventBody): Promise<CalendarEventDetail> {
    const res = await apiFetch<{ event: CalendarEventDetail }>(`/calendar/events/${encodeURIComponent(id)}`, {
      method: 'PUT', body: JSON.stringify(body),
    });
    invalidateCalendar();
    return res.event;
  },
  async remove(id: string, mode: 'this' | 'following' | 'all' = 'all', date?: string): Promise<void> {
    const q = new URLSearchParams({ mode });
    if (date) q.set('date', date);
    await apiFetch(`/calendar/events/${encodeURIComponent(id)}?${q.toString()}`, { method: 'DELETE' });
    invalidateCalendar();
  },
  async respond(id: string, response: CalendarResponse): Promise<void> {
    await apiFetch(`/calendar/events/${encodeURIComponent(id)}/response`, {
      method: 'POST', body: JSON.stringify({ response }),
    });
    invalidateCalendar();
  },
  async share(id: string, channelIds: number[], via?: string | number | null): Promise<ShareResult[]> {
    const res = await apiFetch<{ results: ShareResult[] }>(`/calendar/events/${encodeURIComponent(id)}/share`, {
      method: 'POST', body: JSON.stringify({ channelIds, via: via ?? undefined }),
    });
    return res.results || [];
  },
};

// ─── 범위 캐시 ──────────────────────────────────────────────────────────

interface Entry {
  data: CalendarRangeResult | null;
  error: string | null;
  loading: boolean;
  stale: boolean;
  at: number;
  inflight: Promise<void> | null;
}

const cache = new Map<string, Entry>();
const subscribers = new Map<string, Set<() => void>>();
let generation = 0; // clearCalendarCache 이후 늦게 도착한 응답을 버린다
let refetchTimer: ReturnType<typeof setTimeout> | null = null;
let hiddenAt = 0;
const HIDDEN_REFETCH_MS = 10 * 60 * 1000;
const MAX_ENTRIES = 24;

const keyOf = (from: string, to: string) => `${from}|${to}`;

function notify(key: string) {
  subscribers.get(key)?.forEach(fn => fn());
}

function fetchKey(key: string): Promise<void> {
  const entry = cache.get(key) || { data: null, error: null, loading: false, stale: true, at: 0, inflight: null };
  if (entry.inflight) return entry.inflight;
  const [from, to] = key.split('|');
  const gen = generation;
  entry.loading = true;
  entry.error = null;
  cache.set(key, entry);
  notify(key);
  const p = calendarApi.getRange(from, to)
    .then((data) => {
      if (gen !== generation) return;
      entry.data = data;
      entry.stale = false;
      entry.at = Date.now();
    })
    .catch((err) => {
      if (gen !== generation) return;
      entry.error = err instanceof Error ? err.message : '일정을 불러오지 못했습니다.';
    })
    .finally(() => {
      entry.loading = false;
      entry.inflight = null;
      if (gen === generation) notify(key);
    });
  entry.inflight = p;
  // 오래된 범위 정리 — 구독이 없는 것부터
  if (cache.size > MAX_ENTRIES) {
    for (const k of cache.keys()) {
      if (cache.size <= MAX_ENTRIES) break;
      if (!subscribers.get(k)?.size && k !== key) cache.delete(k);
    }
  }
  return p;
}

/** 캐시 전체를 낡음으로 — 구독 중인 범위만 다시 받는다 */
export function invalidateCalendar(delayMs = 0) {
  cache.forEach(e => { e.stale = true; });
  if (refetchTimer) clearTimeout(refetchTimer);
  refetchTimer = setTimeout(() => {
    refetchTimer = null;
    subscribers.forEach((set, key) => {
      if (set.size > 0) fetchKey(key);
    });
  }, Math.max(0, delayMs));
}

export function clearCalendarCache() {
  generation += 1;
  cache.clear();
  noticeRefs.clear();
  if (refetchTimer) { clearTimeout(refetchTimer); refetchTimer = null; }
}

function subscribe(key: string, fn: () => void): () => void {
  let set = subscribers.get(key);
  if (!set) { set = new Set(); subscribers.set(key, set); }
  set.add(fn);
  return () => { set!.delete(fn); };
}

export interface CalendarRangeState {
  data: CalendarRangeResult | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/** 범위 구독 — 캐시가 있으면 즉시 그리고, 없거나 낡았으면 받는다. from/to가 비면 구독하지 않는다 */
export function useCalendarRange(from: string | null, to: string | null): CalendarRangeState {
  const key = from && to ? keyOf(from, to) : null;
  const read = () => (key ? cache.get(key) : undefined);
  const [, setTick] = useState(0);
  const keyRef = useRef(key);
  keyRef.current = key;

  useEffect(() => {
    if (!key) return;
    const unsub = subscribe(key, () => setTick(n => n + 1));
    const e = cache.get(key);
    if (!e || (e.stale && !e.inflight)) fetchKey(key);
    return unsub;
  }, [key]);

  const e = read();
  return {
    data: e?.data || null,
    loading: !!e?.loading || (!!key && !e),
    error: e?.error || null,
    refresh: () => { if (keyRef.current) fetchKey(keyRef.current); },
  };
}

// 실시간 무효화 배선 — 모듈 로드 시 1회
if (typeof window !== 'undefined') {
  window.addEventListener('calendar:changed', (ev: Event) => {
    const detail = (ev as CustomEvent).detail || {};
    // 회사 일정은 계열사 전원에게 동시에 간다 — 다시 받기를 0~2초에 흩는다
    invalidateCalendar(detail.scope === 'company' ? 300 + Math.floor(Math.random() * 2000) : 300);
  });
  window.addEventListener('socket:reconnected', () => invalidateCalendar(500));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
    if (hiddenAt && Date.now() - hiddenAt > HIDDEN_REFETCH_MS) invalidateCalendar(0);
    hiddenAt = 0;
  });
  // 일정 알림의 이동 참조 — 알림 클릭 경로(토스트·웹 알림·SW)는 알림 id만 들고 온다
  window.addEventListener('activity:new', (ev: Event) => {
    const d = (ev as CustomEvent).detail;
    if (d && d.source === 'calendar' && d.ref) rememberNoticeRef(d.id, d.ref);
  });
}

// ─── 알림 → 캘린더 이동 참조 ────────────────────────────────────────────

const noticeRefs = new Map<number, ActivityRef>();

export function rememberNoticeRef(activityId: number | string, ref: ActivityRef | null | undefined) {
  if (!ref || ref.kind !== 'calendar') return;
  const id = Number(activityId);
  if (!Number.isFinite(id)) return;
  if (noticeRefs.size > 300) noticeRefs.clear();
  noticeRefs.set(id, ref);
}

export function getNoticeRef(activityId: number | string | null | undefined): ActivityRef | null {
  if (activityId === null || activityId === undefined) return null;
  return noticeRefs.get(Number(activityId)) || null;
}

// ─── 화면 요청 스토어 (CalendarModalsHost가 구독) ────────────────────────

export interface CalendarEditorSeed {
  /** 수정 대상(없으면 새 일정) */
  event?: CalendarEventDetail | null;
  /** 새 일정의 기본 날짜(KST 키) */
  date?: string;
  title?: string;
  description?: string;
  attendees?: Array<{ userId: string; name: string }>;
  scope?: CalendarScope;
  /** 대화방에서 열었을 때 — "이 대화방에 공유" 체크 */
  shareChannel?: { id: number; name: string } | null;
}

export interface CalendarDetailRequest {
  eventId: string;
  /** 반복 일정의 회차 날짜(삭제 범위 판정·표시) */
  date?: string;
  /** 공유 메시지로 연 경우 — 읽기 전용 열람 */
  viaMessageId?: string | number | null;
  /** 캘린더 탭이 이미 보이면 '캘린더에서 보기'를 숨긴다 */
  fromCalendar?: boolean;
}

interface UiState {
  editor: (CalendarEditorSeed & { seq: number }) | null;
  detail: (CalendarDetailRequest & { seq: number }) | null;
}

let uiState: UiState = { editor: null, detail: null };
let uiSeq = 0;
const uiListeners = new Set<() => void>();
const emitUi = () => uiListeners.forEach(fn => fn());

export function requestOpenEditor(seed: CalendarEditorSeed = {}) {
  uiSeq += 1;
  uiState = { ...uiState, editor: { ...seed, seq: uiSeq } };
  emitUi();
}
export function closeEditor() {
  uiState = { ...uiState, editor: null };
  emitUi();
}
export function requestOpenDetail(req: CalendarDetailRequest) {
  uiSeq += 1;
  uiState = { ...uiState, detail: { ...req, seq: uiSeq } };
  emitUi();
}
export function closeDetail() {
  uiState = { ...uiState, detail: null };
  emitUi();
}

export function useCalendarUi(): UiState {
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick(n => n + 1);
    uiListeners.add(fn);
    return () => { uiListeners.delete(fn); };
  }, []);
  return uiState;
}

// 캘린더 탭으로 이동 요청(App이 구독) — 홈 카드·알림·공유 카드의 '캘린더에서 보기'
export interface CalendarNavRequest { date: string; eventId?: string | null }
const navListeners = new Set<(req: CalendarNavRequest) => void>();
export function requestOpenCalendarAt(req: CalendarNavRequest) {
  navListeners.forEach(fn => fn(req));
}
export function onCalendarNavRequest(fn: (req: CalendarNavRequest) => void): () => void {
  navListeners.add(fn);
  return () => { navListeners.delete(fn); };
}
